import { optimizedImages } from "@/data/optimized-images";
import { useBooking } from "../booking-context";
import { OptimizedImage } from "../OptimizedImage";
import { ResortButton, ResortLink } from "../resort-button";
import { Eyebrow, MaskReveal, Reveal } from "../reveal";
import { SiteFeatureList } from "./SiteFeatureList";
import { SiteLengthGraphic } from "./SiteLengthGraphic";

const features = [
  "Gravel parking pad",
  "20 / 30 / 50 amp hookups",
  "Water",
  "Sewer",
  "Complimentary WiFi",
  "Picnic table",
] as const;

export function PullThroughSites() {
  const { openBooking } = useBooking();

  return (
    <section
      id="pull-through"
      aria-labelledby="pull-through-heading"
      className="scroll-mt-16 bg-background py-16 md:py-20"
    >
      <div className="shell">
        <div className="grid items-center gap-8 lg:grid-cols-12 lg:gap-10">
          <Reveal className="order-1 lg:col-span-6 lg:order-none" y={36}>
            <OptimizedImage
              image={optimizedImages.sitePullthrough}
              alt="Motorhome positioned on a long pull-through RV site at Auburn RV Resort"
              loading="lazy"
              decoding="async"
              className="aspect-[4/5] w-full object-cover md:aspect-[5/6]"
            />
          </Reveal>

          <div className="order-2 lg:col-span-6 lg:order-none">
            <Reveal>
              <Eyebrow>Easy In. Easy Out.</Eyebrow>
            </Reveal>
            <h2 id="pull-through-heading" className="display-lg mt-6">
              <MaskReveal>Pull-Through RV Sites</MaskReveal>
            </h2>

            <Reveal delay={0.1}>
              <div className="mt-8 space-y-5 text-base leading-relaxed text-muted-foreground">
                <p>
                  Our pull-through sites are ideal for larger rigs or travelers who simply prefer
                  the convenience of arriving and departing without backing into the site. Auburn RV
                  Resort offers 10 pull-through sites in a variety of lengths ranging from
                  approximately 44 feet to 120 feet.
                </p>
                <p>
                  Pull-through sites are available for daily, weekly and monthly stays and include
                  all the essential hookups for a comfortable visit.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.14}>
              <div className="mt-10">
                <p className="eyebrow text-muted-foreground">Site Lengths</p>
                <p className="font-display mt-2 text-4xl tracking-tight text-primary md:text-5xl">
                  44′ – 120′
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.16}>
              <SiteFeatureList features={features} />
            </Reveal>

            <SiteLengthGraphic
              from="44 FT"
              to="120 FT"
              note="Multiple site lengths are available depending on your RV and stay type."
            />

            <Reveal delay={0.2}>
              <div className="mt-10 flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap">
                <ResortButton variant="gold" onClick={openBooking} className="w-full sm:w-auto">
                  Check Pull-Through Availability
                </ResortButton>
                <ResortLink variant="outline" href="/rates/" className="w-full sm:w-auto">
                  View Rates
                </ResortLink>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
